const TARGETING_DIRECTIVES = ["store_whitelist", "store_blacklist"];
const normalizeStoreCode = storeCode => typeof storeCode === "string" ? storeCode.trim().toLowerCase() : "";
const isTargetingKey = key => typeof key === "string" && key.trim().toLowerCase().startsWith("store_");
const toTargetingDirective = (key, value) => {
  const normalizedKey = key.trim().replace(/\|+$/, "");
  const normalizedValue = typeof value === "string" ? value.replace(/^\|+/, "") : "";
  return "".concat(normalizedKey, "||").concat(normalizedValue);
};
const getTargetingMetadata = offer => {
  const flattenedMetadata = Object.entries(offer || {}).filter(_ref => {
    let [key] = _ref;
    return isTargetingKey(key);
  }).map(_ref2 => {
    let [key, value] = _ref2;
    return toTargetingDirective(key, value);
  });
  if (flattenedMetadata.length > 0) {
    return flattenedMetadata.join("\n");
  }
  const publishedOfferDetails = Object.values((offer === null || offer === void 0 ? void 0 : offer.additional_data) || {}).filter(detail => isTargetingKey(detail === null || detail === void 0 ? void 0 : detail.key)).map(detail => toTargetingDirective(detail.key, detail.value));
  if (publishedOfferDetails.length > 0) {
    return publishedOfferDetails.join("\n");
  }

  // Also accept the directive in coupon_details for API variants which expose
  // Published Offer Details as a single text field.
  return offer === null || offer === void 0 ? void 0 : offer.coupon_details;
};
const parseTargetingMetadata = details => {
  if (details == null || details === "") {
    return {
      type: null,
      storeCodes: []
    };
  }
  if (typeof details !== "string") {
    return {
      error: "Published Offer Details must be text"
    };
  }
  const directives = [];
  const lines = details.split(/\r?\n/);
  for (const line of lines) {
    const trimmedLine = line.trim();
    const targetingKey = TARGETING_DIRECTIVES.find(key => trimmedLine.toLowerCase().startsWith(key));
    if (!targetingKey) {
      if (trimmedLine.toLowerCase().startsWith("store_")) {
        return {
          error: "Unsupported store targeting directive: ".concat(trimmedLine)
        };
      }
      continue;
    }
    const match = trimmedLine.match(/^(store_whitelist|store_blacklist)\s*\|\|\s*([^|]+)$/i);
    if (!match) {
      return {
        error: "Malformed store targeting directive: ".concat(trimmedLine)
      };
    }
    const storeCodes = match[2].split(",").map(normalizeStoreCode);
    if (storeCodes.some(code => !code)) {
      return {
        error: "Store targeting directive contains an empty store code: ".concat(trimmedLine)
      };
    }
    directives.push({
      type: match[1].toLowerCase(),
      storeCodes
    });
  }
  if (directives.length === 0) {
    return {
      type: null,
      storeCodes: []
    };
  }
  if (directives.length > 1) {
    return {
      error: "Published Offer contains multiple store targeting directives"
    };
  }
  return directives[0];
};
export const getOfferStoreAvailability = (offer, storeCode) => {
  const targeting = parseTargetingMetadata(getTargetingMetadata(offer));
  if (targeting.error) {
    return {
      available: false,
      reason: targeting.error
    };
  }
  if (!targeting.type) {
    return {
      available: true,
      reason: null
    };
  }
  const normalizedStoreCode = normalizeStoreCode(storeCode);
  if (!normalizedStoreCode) {
    return {
      available: false,
      reason: "Cannot evaluate targeted Published Offer without a current store code"
    };
  }
  const matchesCurrentStore = targeting.storeCodes.includes(normalizedStoreCode);
  const available = targeting.type === "store_whitelist" ? matchesCurrentStore : !matchesCurrentStore;
  return {
    available,
    reason: available ? null : "".concat(targeting.type, " does not allow store ").concat(storeCode)
  };
};
export const isOfferAvailableForStore = function (offer, storeCode) {
  let logger = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : console;
  const availability = getOfferStoreAvailability(offer, storeCode);
  if (!availability.available && logger && typeof logger.warn === "function") {
    logger.warn("Published Offer excluded by store targeting", {
      offerId: offer === null || offer === void 0 ? void 0 : offer.offer_id,
      barcode: offer === null || offer === void 0 ? void 0 : offer.barcode,
      storeCode,
      reason: availability.reason
    });
  }
  return availability.available;
};
export const isCouponCodeAvailableForStore = function (offers, couponCode, storeCode) {
  let logger = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : console;
  const normalizedCouponCode = normalizeStoreCode(couponCode);
  const matchingOffer = (offers || []).find(offer => normalizeStoreCode(offer === null || offer === void 0 ? void 0 : offer.barcode) === normalizedCouponCode);

  // Codes which are not Published Offers retain Magento's existing validation path.
  return matchingOffer ? isOfferAvailableForStore(matchingOffer, storeCode, logger) : true;
};
export { parseTargetingMetadata };